import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useMemo } from "react";
import { matchPath, useLocation, useNavigate, useParams } from "react-router-dom";
import { FolderDetails, Folders, Layout } from "../../components";
import { BreadScrumHeaderHistoryProp } from "../../interfaces";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useConnectChatBotMutation, useLazyGetInstantSearcheByIdQuery, useLinkModuleMutation, useUnlinkModuleMutation } from "../../redux/services";
import { updateAISearchSlice } from "../../redux/slices/ai";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { linksData } from "../../redux/slices/issueTracker";
import { HasAccess } from "../../router/authorization";
import { PERMISSIONS } from "../../router/permission";
import { AppRoutes } from "../../router/routes";
import { LocalStorageKeys } from "../../utils";
import { DataTableForm } from "../viewMaterial/dataSheet";
import { BasicDetails } from "./basicDetails";
import { ChatBox } from "./chatBot";
import { NavBars } from "./utils";

const ViewAIInstantSearch: React.FC<{ children?: JSX.Element, currentPath: string, readOnly?: boolean, showLinkDocumentBtn?: boolean }> = ({ readOnly, showLinkDocumentBtn = true, ...props }) => {

    const params = useParams();
    const navigate = useNavigate();

    const location: any = useLocation();
    const localPaths = (() => {
        const storedValue = sessionStorage.getItem(LocalStorageKeys.locationStatePaths);
        if (storedValue === null || storedValue === "undefined") {
            return []; // Default value when the key is not found or invalid
        }
        try {
            return JSON.parse(storedValue);
        } catch (e) {
            console.error("Failed to parse locationStatePaths:", e);
            return []; // Fallback to default value on parse error
        }
    })()
    const paths = localPaths?.length > 0 ? localPaths : location.state?.paths || [];

    const parentData = location.state?.parentData;
    const object_type = location.state?.object_type;

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [getSearchesById, searchData] = useLazyGetInstantSearcheByIdQuery();
    const [linkDocument] = useLinkModuleMutation();
    const [unlinkDocument] = useUnlinkModuleMutation();
    const [connectChatBot] = useConnectChatBotMutation();

    const [currentPath, setCurrentPath] = React.useState<string>(props.currentPath);
    const [history, setHistory] = React.useState<BreadScrumHeaderHistoryProp[]>([]);

    const searchId = params?.id ?? "New";

    const isEdit = !['New', ':id'].includes(searchId);

    const { perm } = useAppSelector(store => store.auth.userDetails);

    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.AI_SEARCH_CREATE), [perm]);
    const readable = React.useMemo(() => HasAccess(perm, PERMISSIONS.AI_SEARCH_READ), [perm]);
    const updatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.AI_SEARCH_UPDATE), [perm]);
    const deletable = React.useMemo(() => HasAccess(perm, PERMISSIONS.AI_SEARCH_DELETE), [perm]);
    const publishable = React.useMemo(() => HasAccess(perm, PERMISSIONS.AI_SEARCH_UPDATE), [perm]);

    const giveMeAssociations = useMemo(() => {
        return searchData?.data?.associations
    }, [searchData.data?.associations]);

    const giveMeLinksAndUnlinks = (links: any[], unlinks: any[], object_type: string): linksData => {
        let obj: linksData = {
            links: [{ object_ids: links, object_type: object_type }],
            unlinks: [{ object_ids: unlinks, object_type: object_type }],
        };
        return obj;
    };


    const linkForm = async (object_type: string, links?: any[], unlinks?: any[], refetch?: any) => {
        let action = (unlinks || [])?.length > 0 ? "Unlinking" : "Linking";
        let actionSuccess = (unlinks || [])?.length > 0 ? "Unlinked" : "Linked";
        let actionError = (unlinks || [])?.length > 0 ? "Unlink" : "Link";
        dispatch(openBackdrop(`${action} the ${object_type}...`));

        const linkedData = giveMeLinksAndUnlinks(links || [], unlinks || [], object_type);

        let res: any = {};
        try {
            if (action === "Linking") {
                res = await linkDocument({ object_id: searchId, object_type: "instantSearch", payload: { links: linkedData.links } })
            } else {
                res = await unlinkDocument({ object_id: searchId, object_type: "instantSearch", payload: { unlinks: linkedData.unlinks } });
            }

            if (Object.keys(res).includes("data")) {
                enqueueSnackbar(`${actionSuccess} ${object_type} Successfully!`, { variant: "success" })
                dispatch(closeConfirmationDialog());
                if (isEdit) {
                    getSearchesById(searchId);
                }
                refetch && refetch();
            } else if (Object.keys(res).includes("error")) {
                // eslint-disable-next-line
                throw res.error.data;
            }
        } catch (error: any) {
            let errorMessage: string =
                error?.title ?? error ?? `Oops! Something went wrong, Unable to ${actionError} ${object_type}`;
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            dispatch(closeBackdrop());
        }
    };

    const callLinkDocument = async ({ links, refetch, folderDetails }: { links: string[], refetch?: any, folderDetails?: FolderDetails }) => {
        dispatch(openBackdrop(`Linking Document${links.length === 1 ? "" : "s"}...`));

        await Promise.all(
            [...links].map(
                (doc_id: any) => {
                    let folderAssociation = folderDetails ? [{ object_ids: [folderDetails.object_id], object_type: folderDetails.object_type }] : []
                    return linkDocument({
                        object_type: "document", object_id: doc_id, payload: {
                            links: [
                                ...folderAssociation,
                                { object_ids: [searchId], object_type: "instantSearch" }
                            ]
                        }
                    })
                }
            )
        )
            .then(res => {
                const errorRes: any[] = res?.filter((r: any) => r?.error) ?? [];
                if (errorRes?.length > 0) {
                    enqueueSnackbar(`Unable to link the document${links.length === 1 ? "" : "s"}. Info: ${errorRes[0]?.error?.data?.title ?? errorRes[0]?.error?.data ?? "Unknown"}`, { variant: "error" })
                } else {
                    enqueueSnackbar(`Document${links.length === 1 ? "" : "s"} Linked Successfully!!!`, { variant: "success" });
                }
                if (refetch) {
                    refetch();
                }
                connectChatBot({ id: searchId });
            }).catch((err) => {
                enqueueSnackbar(`Unable to link the document${links.length === 1 ? "" : "s"}. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    };

    const callUnLinkDocument = async ({ unlinks, refetch, row }: { unlinks: string[], refetch?: any, row?: any }) => {
        dispatch(openBackdrop(`Unlinking Document${unlinks.length === 1 ? "" : "s"}...`));
        const filteredFolder = row?.associations?.find((a: any) => a?.object_type === "folder")
        await Promise.all(
            unlinks.map(
                (doc_id: any) => {
                    let folderAssociation = filteredFolder ? [{ object_ids: [filteredFolder?.object_details?.[0]?._id], object_type: filteredFolder?.object_type }] : []
                    return unlinkDocument({
                        object_type: "document", object_id: doc_id, payload: {
                            unlinks: [
                                ...folderAssociation,
                                { object_ids: [searchId], object_type: "instantSearch" }
                            ]
                        }
                    })
                }
            )
        )
            .then(res => {
                const errorRes: any[] = res?.filter((r: any) => r?.error) ?? [];
                if (errorRes?.length > 0) {
                    enqueueSnackbar(`Unable to unlink the document${unlinks.length === 1 ? "" : "s"}. Info: ${errorRes[0]?.error?.data?.title ?? errorRes[0]?.error?.data ?? "Unknown"}`, { variant: "error" })
                } else {
                    enqueueSnackbar(`Document${unlinks.length === 1 ? "" : "s"} Unlinked Successfully!!!`, { variant: "success" });
                }
                if (refetch) {
                    refetch();
                }
                connectChatBot({ id: searchId });
            }).catch((err) => {
                enqueueSnackbar(`Unable to unlink the document${unlinks.length === 1 ? "" : "s"}. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    }

    const giveMeComponent = () => {
        // Basic Details
        if (matchPath(location.pathname, AppRoutes.viewAIAllSearcheBasicDetails(searchId))) {
            return <BasicDetails isEdit={isEdit} searchId={searchId} readOnly={readOnly} />
        }

        // Documents
        if (matchPath(location.pathname, AppRoutes.viewAIAllSearcheDocuments(searchId))) {

            return <Folders
                onDocumentUploadComplete={() => connectChatBot({ id: searchId })}
                onRegenerate={() => connectChatBot({ id: searchId })}
                object_id={searchId}
                object_type={'instantSearch'}
                object_sequence_id={searchData?.data?.sequence_id}
                object_association={giveMeAssociations}
                updateHistory={(newHistory, currentPath) => {
                    updateBreadScrumHistory([
                        ...newHistory.map(_ => ({ label: _.label, onClick: () => navigate(location.pathname) }))
                    ])
                    setCurrentPath(currentPath)
                }}
                linkDocument={(links: any, refetch: any, unlinks: string[], type: string, folderDetail: any) => {
                    let folderDetails = folderDetail?.object_type === "folder" ? folderDetail : undefined;
                    callLinkDocument({ links, refetch, folderDetails })
                }}
                updateCurrentPath={(path) => setCurrentPath(path)}
                creatable={creatable}
                updatable={updatable}
                deletable={deletable}
                readable={readable}
                publishable={publishable}
                isPdfOnly={true}
                showHelperBtn={object_type ? showLinkDocumentBtn : true}
                showCreateBtn={true}
                parentData={parentData}
                unlinkDoc={(id: string, refetch: any, row) => callUnLinkDocument({ unlinks: [id], refetch, row })}
            />
        }

        // Chatbox
        if (matchPath(location.pathname, AppRoutes.viewAIAllSearcheChatBox(searchId))) {
            return <ChatBox searchId={searchId} />
        }

        // Data Sheet
        if (matchPath(location.pathname, AppRoutes.viewAIAllSearcheChecklist(searchId))) {
            const sheet_id = searchData?.data?.associations?.find((_: any) => _.object_type === "dataTableForm")?.object_details?.at(-1)?._id || "New";
            return <DataTableForm
                type="checklist"
                associations={searchData?.data?.associations ?? []}
                refetch={() => isEdit && getSearchesById(searchId)}
                getPath={() => ''}
                sheet_id={sheet_id}
                otherDetails={{ searchId }}
                linkDataTableTemplate={async (links: any[], unlinks: any[]) => linkForm('dataTableForm', links, unlinks)}
                info={sheet_id !== "New" ? "" : "Note: Checklist template tables must have three columns 'Description'(Non Editable),'Response'(Editable),'Notes'(Editable)"}
            />
        }

        return <></>
    };

    const updateBreadScrumHistory = (appendHistory: BreadScrumHeaderHistoryProp[] = []) => {
        let breadScrumHistory: BreadScrumHeaderHistoryProp[] = [
            { label: "AI", onClick: () => navigate(AppRoutes.aiInstantSearch) },
            {
                label: `${searchData.isLoading ? "Loading..." : searchData?.data?.sequence_id ?? "New"}`, onClick: () => navigate(AppRoutes.viewAIAllSearcheBasicDetails(searchId))
            }
        ];

        if (paths?.length > 0) {
            breadScrumHistory = [
                ...(paths ?? []),
                breadScrumHistory[1]
            ]
        }

        setHistory([...breadScrumHistory, ...appendHistory]);
    };

    React.useEffect(() => {
        setCurrentPath(props.currentPath);
        updateBreadScrumHistory();
        // eslint-disable-next-line
    }, [props.currentPath]);

    React.useEffect(() => {
        if (paths?.length > 0) {
            sessionStorage.setItem(
                LocalStorageKeys.locationStatePaths,
                JSON.stringify(location.state?.paths)
            );
        }
        // eslint-disable-next-line
    }, [searchId]);

    React.useEffect(() => {
        if (searchId === ':id') {
            dispatch(closeBackdrop());
            navigate(AppRoutes.aiInstantSearch)
        }
    });

    React.useEffect(() => {
        if (isEdit) {
            getSearchesById(searchId);
        }
        // eslint-disable-next-line    
    }, [isEdit]);

    React.useEffect(() => {
        if (searchData.isLoading) {
            dispatch(openBackdrop("Fetching Search Details..."));
        } else {
            dispatch(closeBackdrop());
        }
        // eslint-disable-next-line
    }, [searchData.isLoading])

    React.useEffect(() => {
        const updateValue = async () => {
            if (isEdit && searchData.isSuccess) {
                dispatch(updateAISearchSlice({
                    sequence_id: searchData?.data?.sequence_id ?? "New",
                    basic_details: { name: searchData.data.name, reason: searchData.data.reason },
                    error: {
                        basic_details: { name: false, reason: false },
                    }
                }))
                updateBreadScrumHistory();
            }
        }
        updateValue();
        // eslint-disable-next-line
    }, [searchData.isSuccess]);

    React.useEffect(() => {
        if (!searchData.isFetching && searchData.isError) {
            enqueueSnackbar('404 - Unable to find the requested Search details', { variant: "error" });
            dispatch(openConfirmationDialog({
                title: "404 - Not Found",
                body: "Unable to find the requested Search details",
                positiveBtn: "Ok",
                onOk: () => {
                    dispatch(closeConfirmationDialog())
                    navigate(AppRoutes.aiInstantSearch)
                },
                hideNegativeBtn: true
            }))
        }
        // eslint-disable-next-line
    }, [searchData.isError])

    return (
        <Box
            sx={{
                p: 0.5,
                height: "100%"
            }}>
            <Layout
                history={history}
                currentPath={currentPath}
                navBars={NavBars(searchId, isEdit)}
                mainPanel={giveMeComponent()}
                sideNavVariant={"whiteboard"}
                hideNavBarByDefault={false}
                locationState={{
                    paths: paths,
                    parentData: parentData,
                    object_type: object_type
                }}
                resizable
            />
        </Box>
    );
}

export default ViewAIInstantSearch;